<template>
  <div class="navigation-products-fi-wrapper">
    <loading-indicator
      v-if="loading"
      variant="spinnerLocal"
      class="navigation-products-fi__loading-indicator spinner-local--rebrush"
    />

    <ul
      v-else
      class="navigation-products-fi"
    >
      <li
        v-for="item in getAllCategories"
        :id="`fi-navigation-${item.CategoryId}`"
        :key="item.CategoryId"
        class="navigation-products-fi__item"
      >
        <a
          :href="getHref(item)"
          class="navigation-products-fi__item--link"
          data-test="category-link"
          @click="onClick($event, item)"
        >
          <navigation-products-item-fi :item="item" />
        </a>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { useQuery } from '@urql/vue';
import { LoadingIndicator } from 'atoms';
import { computed, onServerPrefetch } from 'vue';

import { useEventHub } from '@/composables/eventHub';
import { useFischer } from '@/composables/fischerPlugin';
import { useSalesChannelFilter } from '@/composables/salesChannelFilter';
import type { GetAllCategoriesQuery } from '@/generated/graphql';
import GETALLCATEGORIES from '@/graphql/getAllCategories.gql';
import { useNavigationStore } from '@/store/pinia/navigation';

import NavigationProductsItemFi from './navigation-products-item-fi/NavigationProductsItemFi.vue';

defineOptions({ name: 'NavigationProducts' });

type NavigationCategory = NonNullable<
  NonNullable<GetAllCategoriesQuery['getAllCategories']>[number]
>;

const navigationStore = useNavigationStore();
const fischer = useFischer();
const { sendClickToEventHub } = useEventHub();
const { getSalesChannels, isPublicAssortment } = useSalesChannelFilter();

const groupData = computed(() => navigationStore.groupData);

const variables = computed(() => ({
  baseSiteId: fischer.baseSiteId(),
  language: fischer.catalogLocale(),
  salesChannels: getSalesChannels(),
  publicAssortment: isPublicAssortment(),
}));

const {
  data,
  fetching: loading,
  executeQuery,
} = useQuery<GetAllCategoriesQuery>({
  query: GETALLCATEGORIES,
  variables,
});
onServerPrefetch(async () => {
  await executeQuery();
});

const getAllCategories = computed<NavigationCategory[]>(
  () => (data.value?.getAllCategories?.filter(Boolean) as NavigationCategory[]) ?? [],
);

const getHref = (item: NavigationCategory) => {
  return groupData.value?.url + item.Slug;
};

const onClick = (evt: MouseEvent, item: NavigationCategory) => {
  sendClickToEventHub(
    {
      clickSource: 'navigation',
      familyId: item.CategoryId,
      pageTitle: item.CategoryName ?? undefined,
      query: `[category]:${item.CategoryName}`,
      url: getHref(item),
    },
    evt,
    getHref(item),
  );
};
</script>

<style scoped src="./navigation-products-fi" />
